/**
 * The country-of-origin toggle above the model catalog (#2015).
 *
 * One button: hide models whose maker is based in a country the US designates
 * a foreign adversary, and models whose maker we could not identify.
 *
 * **It owns no filter state.** It is handed whether it is on and reports the
 * flip; the page writes the same condition the filter builder writes. Two
 * sources of truth for one filter is how a chip row comes to disagree with the
 * table, and it would make "Clear all" lie.
 *
 * **The wording is the load-bearing part.** This control must not imply that
 * anybody approved anything — a US-built model is not *approved*, it is simply
 * not on the list. The strings here match the binding mockup
 * (`docs/mockups/2015-maker-country-filter.html`) exactly, and the words
 * *approved*, *authorized*, *certified*, *compliant*, *cleared*, *FedRAMP* and
 * *safe* appear in none of them.
 */
import { Info } from 'lucide-react'
import { Tooltip } from '@/components/ui'

/** The designated countries, spelled for an operator. Named individually
 * rather than as "foreign adversaries" alone so the tooltip answers the
 * question directly instead of sending someone to look up a regulation.
 *
 * China covers BOTH the Hong Kong and Macau Special Administrative Regions —
 * an earlier draft named only Hong Kong. */
const EXCLUDED_COUNTRY_TEXT =
  'China, including the Hong Kong and Macau Special Administrative Regions · Cuba · Iran · '
  + 'North Korea · Russia · the Maduro regime'

export interface OriginToggleProps {
  /** Whether the filter is currently applied. */
  active: boolean
  /** How many rows it would hide — excluded countries plus unknown makers. */
  hiddenCount: number
  onToggle: (next: boolean) => void
  /** True when the deployment keeps excluded models out of the catalog
   * entirely (#2107). There is then nothing to filter, so the button is
   * replaced by a statement of what the deployment does. */
  policyActive?: boolean
}

export function OriginToggle({ active, hiddenCount, onToggle, policyActive }: OriginToggleProps) {
  // #2107 — a statement, not a disabled button. A button whose count is
  // permanently zero is a puzzle, and a disabled one implies the filter could
  // be switched back on here, when it is deploy configuration. Going quiet
  // instead would leave this page indistinguishable from a deployment with no
  // policy and an unusually short catalog — and the posture is the one thing
  // an operator has to attest to.
  if (policyActive) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="origin-toggle">
        <span className="text-sm">Country of origin</span>
        <span className="text-sm text-muted-foreground">
          Excluded countries aren't in this catalog
        </span>
        <ExcludedCountriesTooltip stored={false} />
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="origin-toggle">
      <span className="text-sm">Country of origin</span>
      <button
        type="button"
        aria-pressed={active}
        onClick={() => onToggle(!active)}
        className={[
          'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
          active
            ? 'bg-primary/10 text-primary border-primary/45'
            : 'bg-background text-muted-foreground border-border hover:bg-muted',
        ].join(' ')}
      >
        <span>Hide excluded</span>
        <span className="font-normal tabular-nums opacity-75">{hiddenCount}</span>
      </button>
      <ExcludedCountriesTooltip stored />
    </div>
  )
}

/** The information tooltip beside the control.
 *
 * One verb differs between the two states and nothing else: the filter *hides*
 * these models, while a deployment with the policy on *does not store* them.
 * The country list and the closing sentence are identical, because neither
 * depends on which state we are in — and that closing sentence is the one that
 * must never be dropped: nothing here says the remaining models are approved
 * for anything. */
function ExcludedCountriesTooltip({ stored }: { stored: boolean }) {
  return (
    <Tooltip
      // The accessible name has to match what the control IS. With the policy
      // on there is no filter to explain.
      triggerLabel={
        stored ? 'About the country-of-origin filter' : 'About excluded countries of origin'
      }
      content={
        <span>
          <strong>
            {stored
              ? 'Hides models whose country of origin is:'
              : 'This system does not store models whose country of origin is:'}
          </strong>
          <br />
          {EXCLUDED_COUNTRY_TEXT}
          <br />
          <br />
          <strong>and models whose country of origin we could not determine.</strong>
          <br />
          <br />
          These are the countries the US names as foreign adversaries. This does not mean the
          models left are approved for any particular use.
        </span>
      }
    >
      <Info aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
    </Tooltip>
  )
}
